import { useState, useMemo } from 'react'
import { useRenewal } from '../../../context/renewal'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultSubtitle,
  DefaultTitle,
} from '../../../components/application/defaultComponents'
import { FlexDiv, FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import OptionButton from 'src/components/basic/OptionButton'
import StandardCheckbox from 'src/components/basic/StandardCheckbox'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'
import { LeftBar } from 'src/components/basic/LeftBar'
import { RenewalData } from 'src/dtos/renewalData'
import { AttorneyData } from 'src/dtos/applicationData'
import { ValidationError } from 'yup'
import { format, isValid, parseISO } from 'date-fns'
import { validateDateField } from 'src/utils/formDataManipulation'
import { FirmAttorneysLeftSchema } from 'src/yup/RenewalSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

const Left = () => {
  const { renewalData } = useRenewal()  
  const [hasAttorneysLeft, setHasAttorneysLeft] = useState(renewalData?.renewalSection?.hasLeavingAttorneys ?? undefined)
  const [attorneysLeft, setAttorneysLeft] = useState(() => {
    return renewalData?.renewalSection?.departingAttorneys?.map(dep => ({ ...dep, leavingDate: dep.leavingDate ? format(parseISO(dep.leavingDate), 'MM/dd/yyyy') : undefined })) ?? []
  })
  const [displayErrors, setDisplayErrors] = useState(false)

  const attorneyFullName = ({ firstName, lastName }: { firstName?: string, lastName?: string }) => {
    return [firstName, lastName].join(' ')
  }

  const handleClickAttorney = (attorney: AttorneyData) => {
    setAttorneysLeft(prev => {
      const existingAttorney = prev.find(att => att.attorneyName === attorneyFullName(attorney))
      if (existingAttorney) {
        return prev.filter(att => att.attorneyName !== attorneyFullName(attorney))
      }

      return [...prev,
      {
        attorneyName: attorneyFullName(attorney),
        leavingDate: '',
        emailAddress: attorney?.emailAddress,
        firstName: attorney?.firstName,
        lastName: attorney?.lastName
      }
      ]
    })
  }

  const isAttorneyChecked = (name: string) => {
    return !!attorneysLeft.find(att => att.attorneyName === name)
  }

  const leavingAttorneyIndex = (name: string) => {
    return attorneysLeft.map(att => att.attorneyName).indexOf(name);
  }

  const handleInputChange = ({ attorneyName, leavingDate, joinedDate, emailAddress, firstName, lastName }: { attorneyName: string, leavingDate?: string, joinedDate?: string, emailAddress?: string, firstName?: string, lastName?: string }) => {
    setAttorneysLeft(prev => {
      const existingIndex = prev.findIndex(att => att.attorneyName === attorneyName)

      if (existingIndex >= 0) {
        let nextState = [...prev]
        nextState[existingIndex] = { attorneyName, leavingDate, joinedDate, emailAddress, firstName, lastName }
        return nextState
      }
      return prev
    })
  }

  const updatedApplicationData = (): RenewalData => ({
    ...renewalData,
    currentPage: window.location.pathname,
    renewalSection: {
      ...renewalData.renewalSection,
      hasLeavingAttorneys: hasAttorneysLeft,
      departingAttorneys: attorneysLeft.map(att => ({
        ...att,
        leavingDate: (att?.leavingDate && isValid(new Date(att.leavingDate))) ? format(new Date(att.leavingDate), 'yyyy-MM-dd') : undefined
      }))
    }
  })

  const pageValidation = useMemo(() => {
    try {
      FirmAttorneysLeftSchema.validateSync({
        hasLeavingAttorneys: hasAttorneysLeft,
        departingAttorneys: attorneysLeft
      }, { abortEarly: false })
      return { isValid: true, error: '' }
    } catch (err) {
      const { errors } = err as ValidationError
      return { isValid: false, error: errors[0] }
    }
  }, [attorneysLeft, hasAttorneysLeft, FirmAttorneysLeftSchema])

  const handleScrollIfInvalid = () => {
    if (!pageValidation.isValid) {
      setDisplayErrors(true)
      document.getElementById('attorneys__left_title')?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const getInputValue = (name: string) => {
    return attorneysLeft.find(att => att.attorneyName === name)?.leavingDate
  }

  const isFieldInvalid = (errorType: string) => {
    return !pageValidation.isValid && pageValidation.error === errorType && displayErrors
  }

  const goBackRoute = () => {
    const sec1 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt')
    const sec2 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities - Other Types Of Securities Practice')

    if((sec1 || sec2) ){
      return '/customer/renew/firm/securities-law'
    }

    const area = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
    if (area !== undefined && area.types !== undefined && area.types.length > 0) {
      return '/customer/renew/firm/real-estate'
    }
    return '/customer/renew/firm/percentages'
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle id="attorneys__left_title">Have any attorneys left your firm in the last year?</DefaultTitle>
        <DefaultSubtitle>The renewal application is solely for the future term. There's no need to report any attorney who joined and left the firm mid-term.</DefaultSubtitle>
        <FlexDiv margin="64px 0 0 0" gap="16px">
          <OptionButton
            checked={hasAttorneysLeft === true}
            onChange={() => setHasAttorneysLeft(true)}
            isIncorrect={isFieldInvalid('option_choose_required')}
            errorCode='EC25'
          >
            Yes
          </OptionButton>
          <OptionButton
            checked={hasAttorneysLeft === false}
            onChange={() => {
              setHasAttorneysLeft(false)
              setAttorneysLeft([])
            }}
            isIncorrect={isFieldInvalid('option_choose_required')}
            errorCode='EC25'
          >
            No
          </OptionButton>
        </FlexDiv>
        <ValidationErrorMessage errorCode='EC25' />
        {isFieldInvalid('option_choose_required') ? (
          <FormInputErrorMessage>
            This is a required field
          </FormInputErrorMessage>
        ) : null}
        {hasAttorneysLeft === true ? (
          <FlexDiv dir="column" margin="24px 0 0 0" gap="16px">
            <strong>Please select all attorneys that have left.</strong>
            {renewalData?.attorneysSection?.attorneys?.map(attorney => (
              <FlexDiv dir="column" gap="16px" key={attorneyFullName(attorney)}>
                <StandardCheckbox
                  variation="medium"
                  label={attorneyFullName(attorney)}
                  withBg
                  onChange={() => handleClickAttorney(attorney)}
                  checked={isAttorneyChecked(attorneyFullName(attorney))}
                  isIncorrect={isFieldInvalid('select_attorney_required')}
                  errorCode='EC29'
                />
                
                {isAttorneyChecked(attorneyFullName(attorney)) === true ? (
                  <FlexDiv gap="24px" maxWidth='400px'>
                    <LeftBar />
                    <StandardFormatInput
                      label='Date they left firm'
                      maskStyle='date'
                      onValueChange={({ formattedValue }) => handleInputChange({ attorneyName: attorneyFullName(attorney), leavingDate: formattedValue, joinedDate: attorney.workHistory?.joinedDate, emailAddress: attorney.emailAddress, firstName: attorney.firstName, lastName: attorney.lastName })}
                      value={getInputValue(attorneyFullName(attorney)) ?? ''}
                      isIncorrect={isFieldInvalid('leaving_date_required') && !!validateDateField(getInputValue(attorneyFullName(attorney)))}
                      errorMessage={isFieldInvalid('leaving_date_required') ? validateDateField(getInputValue(attorneyFullName(attorney))) : ''}
                      errorCode={`EC30:${leavingAttorneyIndex(attorneyFullName(attorney))}`}
                    />
                  </FlexDiv>
                ) : null}
              </FlexDiv>
            ))}
          </FlexDiv>
        ) : null}
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        nextRoute="/customer/renew/attorneys/joined"
        updatedApplication={updatedApplicationData()}
        isFormValid={pageValidation.isValid}
        beforeSaveContinueClick={handleScrollIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default Left